// Toggle Button Component w/ Icon:
// This component us used to display an icon and have 2 states, active and inactive, with different styling per state.
// The state of this component is handled through the props passed to it.

import { ReactNode, useEffect, useState } from "react";
import styled, { CSSProperties } from "styled-components";
import { hexToRGBA } from "../../utility/hexToRGB";

interface StyledButtonProps {
    $hoverBG: string | undefined;
    $BG: string | undefined;
    $hoverIcon: string | undefined;
    $Icon: string | undefined;
    $TextColor: string | undefined;
    $hoverTextColor: string | undefined;
    $hoverIncreaseBrightness: string | undefined;
}

const StyledButton = styled.button<StyledButtonProps>`
    background-color: ${(props) => props.$BG};
    transition: all 0.1s ease-in-out;
    .toggleBTNIcon {
        color: ${(props) => props.$Icon};
    }
    .toggleBTNText {
        color: ${(props) => props.$TextColor};
    }
    &:hover {
        background-color: ${(props) => props.$hoverBG};

        .toggleBTNIcon {
            color: ${(props) => props.$hoverIcon};
        }
        .toggleBTNText {
            color: ${(props) => props.$hoverTextColor};
        }
        filter: brightness(${(props) => props.$hoverIncreaseBrightness || 1});
    }
    > * {
        pointer-events: none;
    }
`;

interface ToggleIconButtonProps {
    size: { x: number | string | undefined; y: number | string };
    fullWidth?: boolean;
    fullHeight?: boolean;
    icon?: React.ReactNode | ReactNode; 
    iconSize?: number;
    isActive?: boolean;
    text?: string;
    textColor?: string;
    inactiveBGColor?: string;
    activeBGColor?: string;
    inactiveIconColor?: string;
    activeIconColor?: string;
    borderColor?: string;
    borderRadius?: number;
    backgroundOpacity?: number;
    paddingX?: number;
    className?: string;
    style?: CSSProperties;
    disabled?: boolean;
    centerContent?: boolean;
    alt?: string;
    onClick?(e: React.MouseEvent): void;
    onMouseDown?(e: React.MouseEvent): void;
    onContextMenu?(e: React.MouseEvent): void;

    // Hover
    hoverBGInactive?: string;
    hoverBGActive?: string;
    hoverIconInactive?: string;
    hoverIconActive?: string;
    hoverTextColor?: string;
    hoverIncreaseBrightness?: string; // The amount to increase the brightness of the icon when hovered over
}

export default function ToggleIconButton(props: ToggleIconButtonProps) {
    const [colorsFormatted, setColorsFormatted] = useState(ValidateColors());
    const [validatedSize, setValidatedSize] = useState({
        x: !Number.isNaN(props.size.x) ? props.size.x + "px" : props.size.x,
        y: !Number.isNaN(props.size.y) ? props.size.y + "px" : props.size.y,
    });

    useEffect(() => {
        ValidateSize();
    }, [props]);

    useEffect(() => {
        setColorsFormatted(ValidateColors());
    }, [props]);

    const ButtonStyle: React.CSSProperties = {
        width: validatedSize.x,
        height: validatedSize.y,
        borderRadius: props.borderRadius + "px",
        paddingLeft: props.paddingX + "px",
        paddingRight: props.paddingX + "px",
        border: props.borderColor ? `1px solid ${props.borderColor}` : "",
        alignItems: "center",
        justifyContent:
            props.centerContent === undefined || props.centerContent === true
                ? "center"
                : "",
        opacity: props.disabled ? ".5" : "1",
        ...props.style,
    };

    return (
        <StyledButton
            aria-label={props.alt}
            $BG={
                props.backgroundOpacity
                    ? hexToRGBA(
                          colorsFormatted.BG || "",
                          props.backgroundOpacity
                      )
                    : colorsFormatted.BG
            }
            $Icon={colorsFormatted.IconColor}
            $hoverBG={
                props.backgroundOpacity
                    ? hexToRGBA(
                          colorsFormatted.hoverBG || "",
                          props.backgroundOpacity
                      )
                    : colorsFormatted.hoverBG
            }
            $hoverIncreaseBrightness={props.hoverIncreaseBrightness || "1"}
            $hoverIcon={colorsFormatted.hoverIconColor}
            $TextColor={props.textColor || "white"}
            $hoverTextColor={props.hoverTextColor || ""}
            tabIndex={-1}
            disabled={props.disabled}
            className={`flex min-w-4 flex-shrink-0 flex-grow-0 rounded ${props.className}`}
            style={ButtonStyle}
            onClick={(e: React.MouseEvent) => {
                if (props.onClick === undefined) {
                    console.log("On click has not been implemented.");
                    return;
                }
                e.stopPropagation();
                e.preventDefault();
                props.onClick(e);
            }}
            onMouseDown={(e: React.MouseEvent) => {
                props.onMouseDown?.(e);
            }}
            onContextMenu={(e: React.MouseEvent) => {
                if (props.onContextMenu === undefined) return;
                else {
                    props.onContextMenu?.(e);
                    e.stopPropagation();
                    e.preventDefault();
                }
            }}
            onDragOver={(e: React.DragEvent<HTMLButtonElement>) => {
                e.preventDefault();
                // e.stopPropagation();
            }}
        >
            {props.icon !== undefined && (
                <div
                    className="toggleBTNIcon flex transition-all duration-75 ease-in-out"
                    style={{
                        fontSize: props.iconSize,
                        marginRight: props.text && props.icon ? "7px" : "",
                    }}
                >
                    {props.icon}
                </div>
            )}
            <p
                className="toggleBTNText whitespace-nowrap text-xs opacity-90"
                // style={{ color: props.textColor || "white" }}
            >
                {props.text}
            </p>
        </StyledButton>
    );

    function ValidateSize() {
        let valSize = { x: props.size.x + "px", y: props.size.y + "px" };
        if (props.size.x === undefined) {
            if (props.fullWidth) {
                valSize.x = "100%";
            } else {
                valSize.x = "auto";
            }
        }
        if (props.size.y === undefined) {
            if (props.fullHeight) {
                valSize.y = "100%";
            } else {
                valSize.y = "auto";
            }
        }
        setValidatedSize(valSize);
    }

    function ValidateColors() {
        let [hoverBG, BG, hoverIconColor, IconColor]: (string | undefined)[] =
            [];
        if (props.disabled) {
            hoverIconColor = props.inactiveIconColor;
            hoverBG = props.inactiveBGColor;
            IconColor = props.inactiveIconColor;
            BG = props.inactiveBGColor;
            return { hoverBG, BG, hoverIconColor, IconColor };
        }

        if (props.isActive) {
            hoverIconColor = props.hoverIconActive || props.activeIconColor;
            hoverBG = props.hoverBGActive || props.activeBGColor;
        } else {
            hoverIconColor = props.hoverIconInactive || props.inactiveIconColor;
            hoverBG = props.hoverBGInactive || props.inactiveBGColor;
        }

        if (props.isActive) {
            IconColor = props.activeIconColor;
            BG = props.activeBGColor;
        } else {
            IconColor = props.inactiveIconColor;
            BG = props.inactiveBGColor;
        }

        return { hoverBG, BG, hoverIconColor, IconColor };
    }
}
